import { useEffect } from "react";

import { isAuthLost, sessionLost } from "../auth/authRecovery";
import { isChunkLoadError } from "../utils/lazyWithRetry";
import Error from "./Error";
import { RouteLoading } from "./Loading";

type FallbackProps = {
  error: unknown;
  componentStack: string | null;
  resetError: () => void;
};

/** A 401 that reached render: thrown as-is, or the page fell over on its absent data. */
const isAuthFailure = (error: unknown): boolean =>
  isAuthLost() || (error as { response?: { status?: number } })?.response?.status === 401;

/**
 * What the route error boundary (AppLayout) renders. A real error shows
 * Error.tsx. An auth failure is not a crash: the login redirect has started
 * (src/auth/authRecovery.ts), so this holds the page on the loading view until
 * the browser leaves. A page whose code chunk is gone after a deploy, still gone
 * after the one automatic reload (src/utils/lazyWithRetry.ts), offers a Reload.
 */
const BoundaryFallback = (props: FallbackProps) => {
  const auth = isAuthFailure(props.error);
  useEffect(() => {
    if (auth) sessionLost();
  }, [auth]);
  if (auth) return <RouteLoading />;
  if (isChunkLoadError(props.error)) return <Error {...props} variant="new-version" />;
  return <Error {...props} />;
};

export default BoundaryFallback;
